import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useLeagueMembers } from "../../hooks/league/useFetchLeagueMembers";
import Loading from "../../components/common/Loading";
import LeagueMemberDetail from "./LeagueMemberDetail";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faTrophy,
  faUser,
} from "@fortawesome/free-solid-svg-icons";

const LeagueStandings = ({
  preview,
  leagueId: propLeagueId,
  hideHeader = false,
}) => {
  // Use the league ID from props if provided, otherwise fall back to the Redux store
  const reduxLeagueId = useSelector((state) => state.league.leagueId);
  const leagueId = propLeagueId || reduxLeagueId;

  const [page, setPage] = useState(1); // Pagination state
  const limit = preview ? 3 : 10; // Show top 3 members for preview, otherwise paginate with 10 members per page

  const {
    data: leagueMembersData,
    isLoading: isLeagueMembersLoading,
    error: isLeagueMembersError,
  } = useLeagueMembers(leagueId, page, limit);

  if (isLeagueMembersLoading) return <Loading fullScreen={false} />;
  if (isLeagueMembersError)
    return (
      <div className="p-4 bg-red-50 text-deep-red rounded-xl text-center border border-red-100">
        <div className="text-sm font-medium">Unable to load standings</div>
        <div className="text-xs text-red-600 mt-1">
          {isLeagueMembersError.message}
        </div>
      </div>
    );

  const { data: leagueMembers, totalPages } = leagueMembersData;

  const getRankIcon = (position) => {
    if (position === 1) return "🥇";
    if (position === 2) return "🥈";
    if (position === 3) return "🥉";
    return null;
  };

  return (
    <div className="w-full">
      {!preview && !hideHeader && (
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-deep-red flex items-center justify-center">
            <FontAwesomeIcon icon={faTrophy} className="text-white w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-dark-grey">
              League Standings
            </h2>
            <p className="text-sm text-medium-grey">
              Current rankings and scores
            </p>
          </div>
        </div>
      )}

      <div className="w-full">
        {leagueMembers.length > 0 ? (
          <div className="space-y-2">
            {leagueMembers.map((member, index) => {
              const position = preview
                ? index + 1
                : (page - 1) * limit + index + 1;
              const isTopThree = position <= 3;
              const rankIcon = getRankIcon(position);

              return (
                <div
                  key={member.userId}
                  className={`flex items-center p-3 rounded-xl transition-all duration-200 hover:shadow-sm
                    ${
                      isTopThree && !preview
                        ? "bg-gradient-to-r from-gold/5 to-amber-50 border border-gold/20"
                        : "bg-gray-50 hover:bg-gray-100 border border-gray-100"
                    }`}
                >
                  {/* Rank */}
                  <div className="flex items-center justify-center w-10 h-10 flex-shrink-0 mr-3">
                    {rankIcon ? (
                      <div className="text-lg">{rankIcon}</div>
                    ) : (
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                        ${
                          isTopThree
                            ? "bg-gold text-dark-grey"
                            : "bg-white border border-gray-200 text-medium-grey"
                        }`}
                      >
                        {position}
                      </div>
                    )}
                  </div>

                  {/* Member Details */}
                  <div className="flex-1 min-w-0">
                    <LeagueMemberDetail
                      memberId={member.userId}
                      user={member.user}
                      totalScore={member.points}
                      placement={position}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          !preview && (
            <div className="bg-gray-50 rounded-xl border border-gray-100 p-6 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon
                  icon={faUser}
                  className="text-medium-grey w-6 h-6"
                />
              </div>
              <h3 className="font-semibold text-dark-grey mb-2">
                No Standings Yet
              </h3>
              <p className="text-medium-grey text-sm leading-relaxed">
                Standings will appear after your first event is completed and
                scores are calculated.
              </p>
            </div>
          )
        )}
      </div>

      {/* Pagination Controls (Only if not in preview mode) */}
      {!preview && leagueMembers.length > 0 && totalPages > 1 && (
        <div className="flex justify-center items-center mt-6 gap-3">
          <button
            className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            aria-label="Previous page"
          >
            <FontAwesomeIcon
              icon={faChevronLeft}
              className="text-dark-grey w-4 h-4"
            />
          </button>

          <div className="px-4 py-2 bg-gray-100 rounded-full">
            <span className="text-sm font-medium text-dark-grey">
              {page} of {totalPages}
            </span>
          </div>

          <button
            className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            aria-label="Next page"
          >
            <FontAwesomeIcon
              icon={faChevronRight}
              className="text-dark-grey w-4 h-4"
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default LeagueStandings;
